import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, ExternalLinkIcon, LockIcon, XIcon } from 'lucide-react';
import type { IntegrationGoal, IntegrationOption } from '../../data/integrations';
import { Button } from '../ui/Button';
interface ConnectIntegrationModalProps {
  goal: IntegrationGoal | null;
  option: IntegrationOption | null;
  onConnect: (goal: IntegrationGoal, option: IntegrationOption) => void;
  onClose: () => void;
}
export function ConnectIntegrationModal({
  goal,
  option,
  onConnect,
  onClose
}: ConnectIntegrationModalProps) {
  const [values, setValues] = useState<Record<string, string>>({});
  const open = Boolean(goal && option);
  const submit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!goal || !option) return;
    onConnect(goal, option);
    setValues({});
  };
  const close = () => {
    setValues({});
    onClose();
  };
  return <AnimatePresence data-id="element-572" data-mp="6tpop1otrbc1">
      {open && goal && option && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-573" data-mp="1ilslsq2mvcto">
          <motion.button aria-label="Close connection setup" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={close} className="absolute inset-0 bg-black/70 backdrop-blur-sm" data-id="element-574" data-mp="1do94tiejwly1" />
          <motion.section initial={{
        opacity: 0,
        scale: 0.97,
        y: 12
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.97,
        y: 12
      }} transition={{
        type: 'spring',
        stiffness: 310,
        damping: 28
      }} role="dialog" aria-modal="true" aria-labelledby="connect-integration-title" className="relative z-10 flex max-h-[88vh] w-full max-w-lg flex-col overflow-hidden rounded-3xl border border-white/[0.09] bg-ink-900 shadow-2xl" data-id="element-575" data-mp="zng9wwkc7oue">
            <header className="flex items-start justify-between gap-3 border-b border-white/[0.06] p-5" data-id="element-576" data-mp="2g1ooa3uyeruy">
              <div data-id="element-577" data-mp="1cr2fblra9uyc">
                <div className="mb-2 text-[11px] font-medium uppercase tracking-[0.14em] text-accent-soft" data-id="element-578" data-mp="1ctoi7838bma8">
                  {goal.goal}
                </div>
                <h2 id="connect-integration-title" className="text-[20px] font-semibold tracking-tight text-white" data-id="element-579" data-mp="1mqt6pekjrtu7">
                  Connect {option.name}
                </h2>
                <p className="mt-1 text-[12.5px] leading-relaxed text-zinc-500" data-id="element-580" data-mp="u1m2byyewa11">{option.bestFor}</p>
              </div>
              <button type="button" onClick={close} aria-label="Close" className="rounded-lg p-1.5 text-zinc-400 transition-colors hover:bg-white/[0.06] hover:text-white" data-id="element-581" data-mp="z0p7djee6g90">
                <XIcon className="h-5 w-5" data-id="element-582" data-mp="babfbsvt4hef" />
              </button>
            </header>

            <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col" data-id="element-583" data-mp="b8ickoobehbp">
              <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-5" data-id="element-584" data-mp="fa3krwpwm7fk">
                <div className="rounded-xl border border-white/[0.07] bg-ink-800 p-3.5" data-id="element-585" data-mp="7r48eb0v1g5p">
                  <div className="text-[11px] font-medium uppercase tracking-wide text-zinc-400" data-id="element-586" data-mp="2udnnae41wdy">
                    Before you start
                  </div>
                  <p className="mt-1.5 text-[12.5px] leading-relaxed text-zinc-300" data-id="element-587" data-mp="3py0k5b1fwce4">{option.youNeed}</p>
                </div>

                <div data-id="element-588" data-mp="3kz771jq2eneh">
                  <div className="mb-2 text-[11px] font-medium uppercase tracking-wide text-zinc-400" data-id="element-589" data-mp="3kzpap1f243wf">
                    What to do
                  </div>
                  <ol className="space-y-2" data-id="element-590" data-mp="3b8un9tq2qy9e">
                    {option.steps.map((step, index) => <li key={step} className="flex gap-2.5 text-[12.5px] leading-relaxed text-zinc-300" data-id="element-591" data-mp="3782et2p10xe6" data-mpi={index}>
                        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent/15 text-[10.5px] font-semibold text-accent-soft" data-id="element-592" data-mp="328phtp4fy9xv">
                          {index + 1}
                        </span>
                        {step}
                      </li>)}
                  </ol>
                  <a href={option.helpUrl} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-[12px] font-medium text-accent-soft transition-colors hover:text-white" data-id="element-593" data-mp="164rqnsgsi4f7">
                    Open {option.name} <ExternalLinkIcon className="h-3.5 w-3.5" data-id="element-594" data-mp="ii37l3g1o5x0" />
                  </a>
                </div>

                <div className="space-y-3" data-id="element-595" data-mp="mezcrcatmv6j">
                  {option.fields.map(field => <label key={field.key} className="block" data-id="element-596" data-mp="8jvtqth1y3z8">
                      <span className="mb-1.5 flex items-center gap-1.5 text-[12px] font-medium text-zinc-400" data-id="element-597" data-mp="dh5qyvaonjxm">
                        {field.secret && <LockIcon className="h-3.5 w-3.5 text-zinc-600" data-id="element-598" data-mp="22tr3zlw8dwe9" />}
                        {field.label}
                      </span>
                      <input required type={field.secret ? 'password' : 'text'} value={values[field.key] ?? ''} onChange={event => setValues(current => ({
                  ...current,
                  [field.key]: event.target.value
                }))} placeholder={field.placeholder} className="w-full rounded-xl border border-white/[0.1] bg-ink-850 px-3 py-2.5 text-[13px] text-white outline-none placeholder:text-zinc-600 focus:border-accent/70 focus:ring-2 focus:ring-accent/15" data-id="element-599" data-mp="3sbwe1i1i4c10" />
                      {field.helper && <span className="mt-1.5 block text-[11px] leading-relaxed text-zinc-600" data-id="element-600" data-mp="otwb4hasywu9">{field.helper}</span>}
                    </label>)}
                </div>

                <p className="rounded-xl border border-white/[0.06] bg-ink-800 px-3.5 py-3 text-[11px] leading-relaxed text-zinc-500" data-id="element-601" data-mp="2qp6p0z5mwfwh">
                  Keys are stored on your account, never shown again, and can be disconnected at any time.
                </p>
              </div>

              <footer className="flex justify-end gap-2 border-t border-white/[0.06] p-4" data-id="element-602" data-mp="3fe9lqj14v5jw">
                <Button variant="ghost" size="sm" type="button" onClick={close} data-id="element-603" data-mp="3kbrnnpgqgkie">
                  Cancel
                </Button>
                <Button variant="primary" size="sm" type="submit" data-id="element-604" data-mp="3ub8tc6vziyed">
                  <CheckIcon className="h-3.5 w-3.5" data-id="element-605" data-mp="3ub0fabo4ln1l" /> Connect {option.name}
                </Button>
              </footer>
            </form>
          </motion.section>
        </div>}
    </AnimatePresence>;
}